import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingBackdrop from 'components/LoadingBackdrop';
import useInstanceSettings, { InstanceSetting } from './useInstanceSettings';

function InstanceSettings() {
  const { state, handler } = useInstanceSettings();
  const [deleteTarget, setDeleteTarget] = useState<{
    app: string;
    key: string;
  } | null>(null);

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      if (newRow.value === oldRow.value) {
        return oldRow;
      }
      const success = await handler.onSaveCell(
        newRow.app,
        newRow.key,
        newRow.value
      );
      return success ? newRow : oldRow;
    },
    [handler]
  );

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await handler.onDelete(deleteTarget.app, deleteTarget.key);
    } catch {
      // Error already handled in hook
    }
    setDeleteTarget(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'app',
      headerName: 'App',
      width: 150,
      editable: false,
    },
    {
      field: 'key',
      headerName: 'Key',
      width: 200,
      editable: false,
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 1,
      minWidth: 200,
      editable: true,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 170,
      editable: false,
      valueFormatter: (params: { value: string }) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<InstanceSetting>) => (
        <IconButton
          size="small"
          color="error"
          onClick={() =>
            setDeleteTarget({ app: params.row.app, key: params.row.key })
          }
          aria-label="delete setting"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (!state.isAdmin) {
    return null;
  }

  return (
    <Box
      component="main"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 } }}
    >
      {/* Title */}
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Instance Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage configuration for this tenant instance.
        </Typography>
      </Box>

      {/* Control Bar */}
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="filter-app-label">Filter by app</InputLabel>
            <Select
              labelId="filter-app-label"
              value={state.filterApp}
              label="Filter by app"
              onChange={(e) => handler.onFilterChange(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {state.appOptions.map((app) => (
                <MenuItem key={app} value={app}>
                  {app}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <IconButton onClick={handler.onRefresh} aria-label="refresh settings">
            <RefreshIcon />
          </IconButton>
        </Grid>
      </Grid>

      {/* Settings Table */}
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={state.settings}
          columns={columns}
          getRowId={(row) => row.id}
          autoHeight
          rowHeight={44}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          processRowUpdate={processRowUpdate}
          experimentalFeatures={{ newEditingApi: true }}
          loading={state.isLoading}
          sx={{
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Paper>

      {/* Add New Setting */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add Setting
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="App"
              size="small"
              fullWidth
              value={state.newSetting.app}
              onChange={(e) => handler.onNewSettingChange('app', e.target.value)}
              placeholder="e.g. general, canmail"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Key"
              size="small"
              fullWidth
              value={state.newSetting.key}
              onChange={(e) => handler.onNewSettingChange('key', e.target.value)}
              placeholder="e.g. sending_domain"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Value"
              size="small"
              fullWidth
              value={state.newSetting.value}
              onChange={(e) =>
                handler.onNewSettingChange('value', e.target.value)
              }
              placeholder="Setting value"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handler.onAddSetting}
              fullWidth
              sx={{ height: '100%' }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete Setting</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.app}.{deleteTarget?.key}</strong>? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={state.isLoading} />
    </Box>
  );
}

export default InstanceSettings;
